/**
 * D3GraphCanvas — D3 + SVG graph renderer for Environment.
 *
 * Ports the v3 prototype's D3 rendering (node shapes, force simulation,
 * edge rendering, interactions) into a React component. D3 owns the SVG
 * rendering via useEffect; React owns the container and callbacks.
 *
 * The node shapes, force parameters, edge styles, and interaction handlers
 * are extracted directly from echelon_environment_knowledge_graph_v3.html.
 *
 * Cycle 074.
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';

// ── Node Classes (from v3 prototype) ──

export const NODE_CLASSES: Record<string, { color: string; dim: string; label: string; shape: string; size: number }> = {
  primitive:     { color: 'oklch(0.570 0.158 210)', dim: 'oklch(0.205 0.050 210)', label: 'Primitive',     shape: 'circle',    size: 14 },
  construct:     { color: 'oklch(0.530 0.205 295)', dim: 'oklch(0.235 0.065 295)', label: 'Construct',     shape: 'hex',       size: 18 },
  // Investigation = pentagon per the design handoff (`environment/tokens.jsx:23`)
  // + rail projection (`canvas/nodeShapes/nodeMaps.ts:39`) — sprint-236 GAP-1.
  investigation: { color: 'oklch(0.548 0.172 198)', dim: 'oklch(0.192 0.055 198)', label: 'Investigation', shape: 'pentagon',  size: 22 },
  knowledge:     { color: 'oklch(0.510 0.168 278)', dim: 'oklch(0.222 0.055 278)', label: 'Knowledge',     shape: 'roundrect', size: 20 },
  run:           { color: 'oklch(0.568 0.160 152)', dim: 'oklch(0.248 0.072 152)', label: 'Run',           shape: 'diamond',   size: 14 },
  attestation:   { color: 'oklch(0.688 0.165 72)',  dim: 'oklch(0.275 0.065 72)',  label: 'Attestation',   shape: 'star',      size: 16 },
  branch:        { color: 'oklch(0.655 0.178 52)',  dim: 'oklch(0.255 0.062 52)',  label: 'Branch',        shape: 'triangle',  size: 13 },
  failed:        { color: 'oklch(0.615 0.208 22)',  dim: 'oklch(0.225 0.065 22)',  label: 'Failed',        shape: 'cross',     size: 12 },
  signal:        { color: 'oklch(0.658 0.148 195)', dim: 'oklch(0.218 0.052 195)', label: 'Signal',        shape: 'octagon',   size: 13 },
  hub:           { color: 'oklch(0.530 0.205 295)', dim: 'oklch(0.235 0.065 295)', label: 'Hub',           shape: 'circle',    size: 28 },
  // Backend-contract aliases
  theatre:       { color: 'oklch(0.530 0.205 295)', dim: 'oklch(0.235 0.065 295)', label: 'Theatre',       shape: 'circle',    size: 28 },
  template:      { color: 'oklch(0.530 0.205 295)', dim: 'oklch(0.235 0.065 295)', label: 'Construct',     shape: 'hex',       size: 18 },
  // Composition nodes (graph-native since 077)
  module:        { color: 'oklch(0.548 0.172 198)', dim: 'oklch(0.192 0.055 198)', label: 'Module',        shape: 'hex',       size: 16 },
  source:        { color: 'oklch(0.570 0.158 210)', dim: 'oklch(0.205 0.050 210)', label: 'Source',        shape: 'circle',    size: 14 },
};

// ── Edge Types (from v3 prototype) ──

const EDGE_TYPES: Record<string, { color: string; dasharray: string | null; width: number; opacity: number }> = {
  feeds:         { color: 'oklch(0.668 0.162 175)', dasharray: null,   width: 2.4, opacity: 0.82 },
  lineage:       { color: 'oklch(0.440 0.110 258)', dasharray: '3,3',  width: 1.0, opacity: 0.42 },
  orchestration: { color: 'oklch(0.530 0.205 295)', dasharray: null,   width: 1.5, opacity: 0.55 },
  ingress:       { color: 'oklch(0.570 0.158 210)', dasharray: null,   width: 1.1, opacity: 0.50 },
  proof:         { color: 'oklch(0.688 0.165 72)',  dasharray: null,   width: 1.1, opacity: 0.52 },
  branch:        { color: 'oklch(0.655 0.178 52)',  dasharray: '5,3',  width: 1.1, opacity: 0.48 },
  dependency:    { color: 'oklch(0.320 0.005 265)', dasharray: '2,4',  width: 1.0, opacity: 0.32 },
  failed:        { color: 'oklch(0.615 0.208 22)',  dasharray: '3,3',  width: 1.0, opacity: 0.35 },
  calibration:   { color: 'oklch(0.658 0.148 195)', dasharray: null,   width: 1.1, opacity: 0.45 },
  // Backend contract edge types — semantic emphasis (077 Sprint 0)
  FEEDS:           { color: 'oklch(0.668 0.162 175)', dasharray: null,    width: 2.0, opacity: 0.82 },  // solid, prominent
  COMPILED_FROM:   { color: 'oklch(0.688 0.165 72)',  dasharray: null,    width: 2.0, opacity: 0.75 },  // solid, gold (proof chain)
  ATTESTS:         { color: 'oklch(0.688 0.165 72)',  dasharray: null,    width: 1.5, opacity: 0.70 },  // gold glow
  PROMOTES:        { color: 'oklch(0.440 0.110 258)', dasharray: '4,3',   width: 1.0, opacity: 0.35 },  // dashed, dim (contextual)
  INFORMS:         { color: 'oklch(0.510 0.168 278)', dasharray: '2,3',   width: 1.0, opacity: 0.35 },  // dotted (informational)
  TEMPLATES:       { color: 'oklch(0.568 0.160 152)', dasharray: '5,3',   width: 1.2, opacity: 0.50 },  // dashed, green (reuse)
  SPAWNED_BY:      { color: 'oklch(0.548 0.172 198)', dasharray: null,    width: 1.5, opacity: 0.55 },  // solid, teal
  CALIBRATED_WITH: { color: 'oklch(0.658 0.148 195)', dasharray: '2,3',   width: 1.0, opacity: 0.35 },  // dotted, cyan
};

// ── Node shape SVG paths (from v3 prototype) ──

export function nodePath(type: string, r: number): string | null {
  switch (type) {
    case 'pentagon': {
      // Point-up regular pentagon, same geometry as the rail's
      // `ShapeRenderer.tsx` pentagon (sprint-236 GAP-1).
      const pts: string[] = [];
      for (let i = 0; i < 5; i++) {
        const a = (i * 2 * Math.PI) / 5 - Math.PI / 2;
        pts.push(`${r * Math.cos(a)},${r * Math.sin(a)}`);
      }
      return 'M' + pts.join('L') + 'Z';
    }
    case 'diamond': {
      return `M0,${-r} L${r * 0.75},0 L0,${r} L${-r * 0.75},0 Z`;
    }
    case 'hex': {
      const a = (2 * Math.PI) / 6;
      return d3.range(6).map(i => `${i === 0 ? 'M' : 'L'}${r * Math.cos(i * a - Math.PI / 6)},${r * Math.sin(i * a - Math.PI / 6)}`).join(' ') + 'Z';
    }
    case 'triangle': {
      return `M0,${-r} L${r * 0.866},${r * 0.5} L${-r * 0.866},${r * 0.5} Z`;
    }
    case 'star': {
      const pts: [number, number][] = [];
      for (let i = 0; i < 5; i++) {
        const outerA = (i * 2 * Math.PI) / 5 - Math.PI / 2;
        const innerA = outerA + Math.PI / 5;
        pts.push([r * Math.cos(outerA), r * Math.sin(outerA)]);
        pts.push([r * 0.44 * Math.cos(innerA), r * 0.44 * Math.sin(innerA)]);
      }
      return 'M' + pts.map(p => p.join(',')).join('L') + 'Z';
    }
    case 'octagon': {
      const a = (2 * Math.PI) / 8;
      return d3.range(8).map(i => `${i === 0 ? 'M' : 'L'}${r * Math.cos(i * a - Math.PI / 8)},${r * Math.sin(i * a - Math.PI / 8)}`).join(' ') + 'Z';
    }
    case 'cross': {
      const t = r * 0.35;
      return `M${-t},${-r}L${t},${-r}L${t},${-t}L${r},${-t}L${r},${t}L${t},${t}L${t},${r}L${-t},${r}L${-t},${t}L${-r},${t}L${-r},${-t}L${-t},${-t}Z`;
    }
    case 'shield': {
      const w = r * 0.85;
      const h = r;
      return `M0,${-h} L${w},${-h * 0.3} L${w * 0.7},${h * 0.8} L0,${h} L${-w * 0.7},${h * 0.8} L${-w},${-h * 0.3} Z`;
    }
    case 'roundrect': {
      const hw = r * 1.3; const hh = r * 0.78; const cr = r * 0.22;
      return `M${-hw + cr},${-hh} L${hw - cr},${-hh} Q${hw},${-hh} ${hw},${-hh + cr} L${hw},${hh - cr} Q${hw},${hh} ${hw - cr},${hh} L${-hw + cr},${hh} Q${-hw},${hh} ${-hw},${hh - cr} L${-hw},${-hh + cr} Q${-hw},${-hh} ${-hw + cr},${-hh} Z`;
    }
    default: return null; // circle
  }
}

// ── Graph data types ──

export interface D3GraphNode {
  id: string;
  type: string;
  label: string;
  sublabel?: string;
  status?: string;
  knowledgeState?: string;
  signalCount?: number;
  /** Counter-signal count from the backend evidence bundle (GAP-3 Tier 1).
   * Rendered as a contesting pip ONLY when present and > 0 — honest
   * absent/0 handling, never fabricated. */
  counterSignalCount?: number;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

export interface D3GraphEdge {
  source: string | D3GraphNode;
  target: string | D3GraphNode;
  type: string;
  label?: string;
}

export interface CanvasNodeTypeItem {
  kind: string;
  id?: string;
  label: string;
  category: string;
  description: string;
}

interface D3GraphCanvasProps {
  nodes: D3GraphNode[];
  edges: D3GraphEdge[];
  onNodeSelect?: (node: D3GraphNode | null) => void;
  onNodeDoubleClick?: (node: D3GraphNode) => void;
  /**
   * Layout regime driving the force simulation. `'force'` is kept as a
   * backward-compatible alias for `'structure'` (FR-E-3 / cycle-105.8 S2).
   * The internal layout switcher (Structure / Radial / Spine / Memory)
   * initialises from this prop and then drives the sim itself.
   */
  layout?: 'force' | 'structure' | 'radial' | 'spine' | 'memory';
  /** Called when operator selects a module to place on canvas */
  onPlaceModule?: (moduleKind: string) => void;
  className?: string;
}

// Memory-cluster tier positions (as fraction of canvas height)
const MEMORY_TIERS: Record<string, number> = {
  knowledge: 0.18,    // upper tier
  investigation: 0.45, // middle tier
  hub: 0.75,           // lower tier
  construct: 0.75,
  run: 0.72,
  attestation: 0.80,
  primitive: 0.55,
  signal: 0.55,
  branch: 0.68,
  failed: 0.72,
};

/**
 * Map the `layout` prop to the internal switcher regime label.
 * `'force'` is the backward-compatible alias for `'structure'`.
 * Exported for unit testing the FR-E-3 prop→regime contract.
 */
export function layoutToRegime(layout: string): string {
  switch (layout) {
    case 'memory': return 'Memory Cluster';
    case 'radial': return 'Radial';
    case 'spine': return 'Spine';
    case 'structure':
    case 'force':
    default: return 'Structure';
  }
}

export function D3GraphCanvas({ nodes: inputNodes, edges: inputEdges, onNodeSelect, onNodeDoubleClick, layout = 'force', onPlaceModule, className }: D3GraphCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const simulationRef = useRef<d3.Simulation<D3GraphNode, D3GraphEdge> | null>(null);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const nodeSelRef = useRef<d3.Selection<SVGGElement, D3GraphNode, SVGGElement, unknown> | null>(null);
  const edgeSelRef = useRef<d3.Selection<SVGGElement, D3GraphEdge, SVGGElement, unknown> | null>(null);
  const [physicsActive, setPhysicsActive] = useState(true);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [activeTool, setActiveTool] = useState<'select' | 'pan'>('select');
  const [layoutMenuOpen, setLayoutMenuOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());
  const [activeLayout, setActiveLayout] = useState<string>(() => layoutToRegime(layout));
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const adjRef = useRef<Record<string, Set<string>>>({});

  // Keep the internal switcher in sync when the parent changes the layout prop.
  useEffect(() => {
    setActiveLayout(layoutToRegime(layout));
  }, [layout]);

  useEffect(() => {
    if (!containerRef.current || inputNodes.length === 0) return;

    const container = containerRef.current;
    const W = container.clientWidth;
    const H = container.clientHeight;

    // Clean up previous
    d3.select(container).select('svg').remove();

    const nodes = inputNodes.map(d => ({ ...d }));
    const edges = inputEdges.map(d => ({ ...d }));

    // Build adjacency for hover/focus
    const adj: Record<string, Set<string>> = {};
    nodes.forEach(n => adj[n.id] = new Set());
    edges.forEach(e => {
      const sid = typeof e.source === 'string' ? e.source : e.source.id;
      const tid = typeof e.target === 'string' ? e.target : e.target.id;
      if (adj[sid]) adj[sid].add(tid);
      if (adj[tid]) adj[tid].add(sid);
    });
    adjRef.current = adj;

    // SVG
    const svg = d3.select(container).append('svg')
      .attr('width', W).attr('height', H)
      // Transparent so the page's dot-field depth background shows through
      // behind the graph (FR-E-5 / cycle-105.8 S2).
      .style('background', 'transparent');
    svgRef.current = svg.node();

    // Defs for arrowheads and glows
    const defs = svg.append('defs');
    // Arrow markers
    Object.entries(EDGE_TYPES).forEach(([key, et]) => {
      defs.append('marker')
        .attr('id', `arrow-${key}`)
        .attr('viewBox', '0 0 10 10').attr('refX', 9).attr('refY', 5)
        .attr('markerWidth', 6).attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M 0 2 L 10 5 L 0 8 Z')
        .attr('fill', et.color).attr('fill-opacity', et.opacity * 0.7);
    });
    // Glow filters
    ['purple', 'inv', 'gold', 'green', 'know'].forEach((name, i) => {
      const colors = ['oklch(0.530 0.205 295)', 'oklch(0.548 0.172 198)', 'oklch(0.688 0.165 72)', 'oklch(0.568 0.160 152)', 'oklch(0.510 0.168 278)'];
      const filter = defs.append('filter').attr('id', `glow-${name}`).attr('x', '-50%').attr('y', '-50%').attr('width', '200%').attr('height', '200%');
      filter.append('feGaussianBlur').attr('in', 'SourceGraphic').attr('stdDeviation', '4').attr('result', 'blur');
      filter.append('feFlood').attr('flood-color', colors[i]).attr('flood-opacity', '0.25').attr('result', 'color');
      filter.append('feComposite').attr('in', 'color').attr('in2', 'blur').attr('operator', 'in').attr('result', 'glow');
      const merge = filter.append('feMerge');
      merge.append('feMergeNode').attr('in', 'glow');
      merge.append('feMergeNode').attr('in', 'SourceGraphic');
    });

    // Zoom
    const zoomLayer = svg.append('g').attr('class', 'zoom-layer');
    const edgeGroup = zoomLayer.append('g').attr('class', 'edges');
    const nodeGroup = zoomLayer.append('g').attr('class', 'nodes');

    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 6])
      .on('zoom', (event) => {
        zoomLayer.attr('transform', event.transform);
        setZoomPercent(Math.round(event.transform.k * 100));
      });
    zoomRef.current = zoom;
    svg.call(zoom).on('dblclick.zoom', null);

    // ── Force simulation ──
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(edges).id((d: any) => d.id).distance((d: any) => {
        const st = d.source?.type || 'primitive';
        const tt = d.target?.type || 'primitive';
        if (d.type === 'feeds' || d.type === 'FEEDS') return 90;
        if (st === 'hub' || st === 'theatre' || tt === 'hub' || tt === 'theatre') return 115;
        if (st === 'investigation' || tt === 'investigation') return 95;
        if (st === 'knowledge' || tt === 'knowledge') return 85;
        return 75;
      }).strength((d: any) => (d.type === 'feeds' || d.type === 'FEEDS') ? 0.4 : 0.5))
      .force('charge', d3.forceManyBody().strength((d: any) => {
        if (d.type === 'hub' || d.type === 'theatre') return -500;
        if (d.type === 'investigation') return -280;
        if (d.type === 'knowledge') return -240;
        return -180;
      }))
      .force('collision', d3.forceCollide().radius((d: any) => {
        const cls = NODE_CLASSES[d.type];
        return (cls ? cls.size : 14) + 20;
      }).strength(0.85))
      .alphaDecay(0.022);

    // ── Layout regime (FR-E-3 / cycle-105.8 S2) ──
    // The internal switcher (activeLayout) drives the positioning forces, so
    // selecting a regime re-runs the sim with a *distinct* force config.
    //   Structure       — generic force-directed (default; 'force' alias)
    //   Radial          — concentric rings by node type (+ guide rings)
    //   Spine           — time-on-y; parent sorts nodes chronologically,
    //                     so input index is the time proxy ("prove spine")
    //   Memory Cluster  — vertical memory tiers (+ tier labels)
    const regime = activeLayout;
    const minWH = Math.min(W, H);
    if (regime === 'Radial') {
      const ringFor = (t: string) => {
        if (t === 'hub' || t === 'theatre') return 0;
        if (t === 'investigation') return minWH * 0.18;
        if (t === 'knowledge') return minWH * 0.30;
        if (t === 'run' || t === 'attestation') return minWH * 0.40;
        return minWH * 0.34;
      };
      simulation
        .force('center', d3.forceCenter(W / 2, H / 2).strength(0.05))
        .force('radial', d3.forceRadial<D3GraphNode>((d: any) => ringFor(d.type), W / 2, H / 2).strength(0.35))
        .force('x', d3.forceX(W / 2).strength(0.01))
        .force('y', d3.forceY(H / 2).strength(0.01));
    } else if (regime === 'Spine') {
      const lastIdx = Math.max(1, nodes.length - 1);
      simulation
        .force('center', null)
        .force('x', d3.forceX(W / 2).strength(0.12))
        .force('y', d3.forceY<D3GraphNode>().y((_d: any, i: number) => H * (0.10 + 0.80 * (i / lastIdx))).strength(0.30));
    } else if (regime === 'Memory Cluster') {
      simulation
        .force('center', d3.forceCenter(W / 2, H / 2).strength(0.07))
        .force('x', d3.forceX(W / 2).strength(0.04))
        .force('y', d3.forceY<D3GraphNode>().y((d: any) => H * (MEMORY_TIERS[d.type] ?? 0.5)).strength(0.12));
    } else {
      // Structure (default; 'force' alias)
      simulation
        .force('center', d3.forceCenter(W / 2, H / 2).strength(0.07))
        .force('x', d3.forceX(W / 2).strength(0.04))
        .force('y', d3.forceY(H / 2).strength(0.04));
    }

    // ── Regime background guide layer ──
    const guideLayer = zoomLayer.insert('g', ':first-child').attr('class', 'guide-layer');
    if (regime === 'Memory Cluster') {
      const tierLabels = [
        { label: 'KNOWLEDGE', y: H * 0.08 },
        { label: 'INVESTIGATIONS', y: H * 0.35 },
        { label: 'THEATRES & RUNS', y: H * 0.62 },
      ];
      tierLabels.forEach(({ label, y }) => {
        guideLayer.append('text')
          .attr('x', W / 2).attr('y', y)
          .attr('text-anchor', 'middle')
          .attr('fill', 'oklch(0.280 0.005 265)')
          .attr('fill-opacity', 0.15)
          .attr('font-size', '8px')
          .attr('font-family', "'Inter', system-ui, sans-serif")
          .attr('font-weight', '600')
          .attr('letter-spacing', '0.15em')
          .text(label);
        guideLayer.append('line')
          .attr('x1', W * 0.1).attr('x2', W * 0.9).attr('y1', y + 8).attr('y2', y + 8)
          .attr('stroke', 'oklch(0.225 0.014 265)').attr('stroke-opacity', 0.08)
          .attr('stroke-dasharray', '4,6');
      });
    } else if (regime === 'Radial') {
      // Concentric guide rings at the ring radii.
      [0.18, 0.30, 0.40].forEach((frac) => {
        guideLayer.append('circle')
          .attr('cx', W / 2).attr('cy', H / 2).attr('r', minWH * frac)
          .attr('fill', 'none')
          .attr('stroke', 'oklch(0.225 0.014 265)').attr('stroke-opacity', 0.12)
          .attr('stroke-dasharray', '3,7');
      });
    } else if (regime === 'Spine') {
      // Vertical "prove spine" axis: earlier (top) → later (bottom).
      guideLayer.append('line')
        .attr('x1', W / 2).attr('x2', W / 2).attr('y1', H * 0.06).attr('y2', H * 0.94)
        .attr('stroke', 'oklch(0.688 0.165 72)').attr('stroke-opacity', 0.10)
        .attr('stroke-width', 2).attr('stroke-dasharray', '2,6');
      ([['EARLIER', H * 0.085], ['LATER', H * 0.93]] as Array<[string, number]>).forEach(([label, y]) => {
        guideLayer.append('text')
          .attr('x', W / 2 + 8).attr('y', y)
          .attr('fill', 'oklch(0.380 0.007 265)').attr('fill-opacity', 0.55)
          .attr('font-size', '8px').attr('font-family', "'JetBrains Mono', monospace")
          .attr('letter-spacing', '0.15em').text(label);
      });
    }

    simulationRef.current = simulation;

    // ── Edge rendering ──
    const edgeSel = edgeGroup.selectAll<SVGGElement, D3GraphEdge>('.edge-g')
      .data(edges).enter().append('g').attr('class', 'edge-g');
    edgeSelRef.current = edgeSel;

    // Add CSS for animated flow edges
    const style = document.createElement('style');
    style.textContent = `
      @keyframes feeds-flow { from { stroke-dashoffset: 24; } to { stroke-dashoffset: 0; } }
      @keyframes shimmer { from { stroke-dashoffset: 20; } to { stroke-dashoffset: 0; } }
      @keyframes node-halo-breathe { 0%, 100% { stroke-opacity: 0.05; } 50% { stroke-opacity: 0.34; } }
      .edge-feeds-flow { stroke-dasharray: 8 5; animation: feeds-flow 1.2s linear infinite; }
      .edge-active-flow { stroke-dasharray: 6 4; animation: shimmer 0.8s linear infinite; }
      .node-halo-breathe { animation: node-halo-breathe 2.4s ease-in-out infinite; }
    `;
    container.appendChild(style);

    const edgePaths = edgeSel.append('path')
      .attr('class', (d: any) => (d.type === 'feeds' || d.type === 'FEEDS') ? 'edge-feeds-flow' : '')
      .attr('stroke', (d: any) => EDGE_TYPES[d.type]?.color || '#555')
      .attr('stroke-opacity', (d: any) => EDGE_TYPES[d.type]?.opacity ?? 0.35)
      .attr('stroke-width', (d: any) => EDGE_TYPES[d.type]?.width ?? 1.1)
      .attr('stroke-dasharray', (d: any) => {
        // FEEDS edges use animated dasharray via CSS class, not static
        if (d.type === 'feeds' || d.type === 'FEEDS') return null;
        return EDGE_TYPES[d.type]?.dasharray || null;
      })
      .attr('fill', 'none')
      .attr('marker-end', (d: any) => `url(#arrow-${d.type})`);

    // FEEDS edge labels
    edgeSel.filter((d: any) => d.type === 'feeds' || d.type === 'FEEDS')
      .append('text')
      .attr('fill', 'oklch(0.668 0.162 175)')
      .attr('font-size', '7.5px').attr('font-family', "'JetBrains Mono', monospace")
      .attr('font-weight', '600').attr('text-anchor', 'middle')
      .attr('fill-opacity', 0.7).attr('dy', -3)
      .text('FEEDS');

    // ── Node rendering ──
    const nodeSel = nodeGroup.selectAll<SVGGElement, D3GraphNode>('.node-g')
      .data(nodes).enter().append('g').attr('class', 'node-g')
      .style('cursor', 'pointer');
    nodeSelRef.current = nodeSel;
    nodeSel
      .on('click', (event, d) => { event.stopPropagation(); onNodeSelect?.(d); })
      .on('dblclick', (event, d) => {
        event.stopPropagation(); event.preventDefault();
        setFocusedNodeId(prev => prev === d.id ? null : d.id);
        onNodeDoubleClick?.(d);
      })
      .call(d3.drag<SVGGElement, D3GraphNode>()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x; d.fy = d.y;
        })
        .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null; d.fy = null;
        })
      );

    // Halo ring
    nodeSel.append('circle')
      .attr('r', (d: any) => (NODE_CLASSES[d.type]?.size || 14) + 10)
      .attr('fill', 'none')
      .attr('stroke', (d: any) => NODE_CLASSES[d.type]?.color || '#888')
      .attr('stroke-opacity', 0).attr('stroke-width', 1.5);

    // Breathing halo for live/active nodes (FR-E-5 — CSS animation, no WebGL/NFR-2).
    // Inserted as first child so it sits behind the node's main shape.
    nodeSel.filter((d: any) => d.status === 'running' || d.status === 'active')
      .insert('circle', ':first-child')
      .attr('class', 'node-halo-breathe')
      .attr('r', (d: any) => (NODE_CLASSES[d.type]?.size || 14) + 7)
      .attr('fill', 'none')
      .attr('stroke', (d: any) => NODE_CLASSES[d.type]?.color || '#888')
      .attr('stroke-width', 2)
      .style('pointer-events', 'none');

    // Main shape
    nodeSel.each(function (d: any) {
      const g = d3.select(this);
      const cls = NODE_CLASSES[d.type] || NODE_CLASSES.primitive;
      const r = cls.size;
      const path = nodePath(cls.shape, r);

      if (path) {
        const el = g.append('path').attr('d', path)
          .attr('fill', cls.dim).attr('stroke', cls.color)
          .attr('stroke-width', (d.type === 'hub' || d.type === 'theatre') ? 2 : d.type === 'investigation' ? 2 : d.type === 'knowledge' ? 1.8 : 1.4)
          .attr('stroke-opacity', 0.80);
        if (d.type === 'knowledge' && d.knowledgeState === 'SUPERSEDED') {
          el.attr('stroke-dasharray', '4,2.5').attr('stroke-opacity', 0.45);
        }
      } else {
        g.append('circle').attr('r', r)
          .attr('fill', cls.dim).attr('stroke', cls.color)
          .attr('stroke-width', (d.type === 'hub' || d.type === 'theatre') ? 2.5 : 1.5)
          .attr('stroke-opacity', 0.80);
      }

      // Hub inner ring
      if (d.type === 'hub' || d.type === 'theatre') {
        g.append('circle').attr('r', r * 0.55).attr('fill', 'none')
          .attr('stroke', cls.color).attr('stroke-width', 1.2).attr('stroke-opacity', 0.40);
        g.select('circle').attr('filter', 'url(#glow-purple)');
      }

      // Knowledge state dot
      if (d.type === 'knowledge') {
        const ksDotColor: Record<string, string> = {
          'VALIDATED': 'oklch(0.688 0.165 72)', 'PROVISIONAL': 'oklch(0.655 0.178 52)',
          'SUPERSEDED': 'oklch(0.480 0.007 265)', 'EXPIRED': 'oklch(0.615 0.208 22)',
        };
        g.append('circle').attr('r', 3)
          .attr('fill', ksDotColor[d.knowledgeState || 'PROVISIONAL'] || 'oklch(0.480 0.007 265)')
          .attr('fill-opacity', 0.90);
        if (d.knowledgeState === 'VALIDATED') {
          const el = g.select('path').empty() ? g.select('circle') : g.select('path');
          el.attr('filter', 'url(#glow-know)');
        }
      }

      // Investigation glow + inner dot
      if (d.type === 'investigation') {
        const el = g.select('path').empty() ? g.select('circle') : g.select('path');
        el.attr('filter', 'url(#glow-inv)');
        g.append('circle').attr('r', 3.5)
          .attr('fill', 'oklch(0.548 0.172 198)').attr('fill-opacity', 0.9);
      }

      // Attestation gold glow
      if (d.type === 'attestation') {
        g.select('path').attr('filter', 'url(#glow-gold)');
      }

      // Status dot (top-right)
      if (d.status && !['idle', 'open'].includes(d.status)) {
        const dotColor: Record<string, string> = {
          running: 'oklch(0.568 0.160 152)', completed: 'oklch(0.688 0.165 72)',
          failed: 'oklch(0.615 0.208 22)', verified: 'oklch(0.688 0.165 72)',
          active: 'oklch(0.548 0.172 198)', review: 'oklch(0.668 0.162 175)',
        };
        const color = dotColor[d.status] || null;
        if (color) {
          g.append('circle').attr('cx', r * 0.72).attr('cy', -r * 0.72).attr('r', 3.5)
            .attr('fill', color).attr('stroke', 'oklch(0.092 0.012 265)').attr('stroke-width', 1.2);
        }
      }

      // Signal count badge (investigation nodes only)
      if (d.type === 'investigation' && d.signalCount != null && d.signalCount > 0) {
        const badgeX = -r * 0.72;
        const badgeY = -r * 0.72;
        g.append('circle').attr('cx', badgeX).attr('cy', badgeY).attr('r', 6)
          .attr('fill', 'oklch(0.548 0.172 198)').attr('stroke', 'oklch(0.092 0.012 265)').attr('stroke-width', 1);
        g.append('text').attr('x', badgeX).attr('y', badgeY)
          .attr('text-anchor', 'middle').attr('dy', '0.35em')
          .attr('fill', '#fff').attr('font-size', '7px').attr('font-weight', '700')
          .attr('font-family', "'Inter', system-ui, sans-serif")
          .text(d.signalCount > 99 ? '99+' : String(d.signalCount));
      }

      // Counter-signal pip (GAP-3 Tier 1, sprint-236) — contesting evidence
      // from `evidence_bundle_ref.counter_signal_count`. Rendered bottom-left,
      // rose, ONLY when the backend reports > 0 (honest 0/absent handling).
      if (d.type === 'investigation' && d.counterSignalCount != null && d.counterSignalCount > 0) {
        const pipX = -r * 0.72;
        const pipY = r * 0.72;
        g.append('circle').attr('class', 'counter-signal-pip')
          .attr('cx', pipX).attr('cy', pipY).attr('r', 6)
          .attr('fill', 'oklch(0.615 0.208 22)').attr('stroke', 'oklch(0.092 0.012 265)').attr('stroke-width', 1);
        g.append('text').attr('x', pipX).attr('y', pipY)
          .attr('text-anchor', 'middle').attr('dy', '0.35em')
          .attr('fill', '#fff').attr('font-size', '7px').attr('font-weight', '700')
          .attr('font-family', "'Inter', system-ui, sans-serif")
          .text(d.counterSignalCount > 99 ? '99+' : String(d.counterSignalCount));
      }
    });

    // Labels
    nodeSel.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', (d: any) => (NODE_CLASSES[d.type]?.size || 14) + 13)
      .text((d: any) => d.label)
      .attr('fill', (d: any) => d.type === 'investigation' ? 'oklch(0.720 0.015 198)' : d.type === 'knowledge' ? 'oklch(0.710 0.012 278)' : 'oklch(0.690 0.008 265)')
      .attr('font-size', (d: any) => (d.type === 'hub' || d.type === 'theatre') ? '11px' : '9px')
      .attr('font-weight', (d: any) => (d.type === 'hub' || d.type === 'theatre' || d.type === 'investigation' || d.type === 'knowledge') ? '600' : '500')
      .attr('font-family', "'Inter', system-ui, sans-serif");

    // Sublabels
    nodeSel.filter((d: any) => d.sublabel)
      .append('text').attr('text-anchor', 'middle')
      .attr('dy', (d: any) => (NODE_CLASSES[d.type]?.size || 14) + 23)
      .text((d: any) => d.sublabel)
      .attr('fill', 'oklch(0.320 0.005 265)')
      .attr('font-size', '7.5px').attr('font-family', "'Inter', system-ui, sans-serif");

    // ── Tick ──
    simulation.on('tick', () => {
      edgePaths.attr('d', (d: any) => {
        const sx = d.source.x, sy = d.source.y, tx = d.target.x, ty = d.target.y;
        const dx = tx - sx, dy = ty - sy;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const sr = (NODE_CLASSES[d.source.type]?.size || 14) + 3;
        const tr = (NODE_CLASSES[d.target.type]?.size || 14) + 3;
        const x1 = sx + (dx / dist) * sr, y1 = sy + (dy / dist) * sr;
        const x2 = tx - (dx / dist) * tr, y2 = ty - (dy / dist) * tr;
        if (d.type === 'feeds' || d.type === 'FEEDS') {
          const cx = (x1 + x2) / 2 - (dy / dist) * 18;
          const cy = (y1 + y2) / 2 + (dx / dist) * 18;
          return `M${x1},${y1} Q${cx},${cy} ${x2},${y2}`;
        }
        if (d.type === 'lineage' || d.type === 'PROMOTES' || d.type === 'COMPILED_FROM') {
          const cx = (x1 + x2) / 2 + (dy / dist) * 22;
          const cy = (y1 + y2) / 2 - (dx / dist) * 22;
          return `M${x1},${y1} Q${cx},${cy} ${x2},${y2}`;
        }
        return `M${x1},${y1} L${x2},${y2}`;
      });

      // Position edge labels
      edgeSel.filter((d: any) => d.type === 'feeds' || d.type === 'FEEDS').select('text')
        .attr('x', (d: any) => {
          const sx = d.source.x || 0, tx = d.target.x || 0, sy = d.source.y || 0, ty = d.target.y || 0;
          const dx = tx - sx, dy = ty - sy, dist = Math.sqrt(dx * dx + dy * dy) || 1;
          return (sx + tx) / 2 - (dy / dist) * 18;
        })
        .attr('y', (d: any) => {
          const sx = d.source.x || 0, tx = d.target.x || 0, sy = d.source.y || 0, ty = d.target.y || 0;
          const dx = tx - sx, dy = ty - sy, dist = Math.sqrt(dx * dx + dy * dy) || 1;
          return (sy + ty) / 2 + (dx / dist) * 18;
        });

      nodeSel.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    // Background click deselect
    svg.on('click', () => onNodeSelect?.(null));

    // Fit after settling
    setTimeout(() => {
      const bounds = zoomLayer.node()?.getBBox();
      if (bounds && bounds.width > 0) {
        const padding = 40;
        const scale = Math.min((W - padding * 2) / bounds.width, (H - padding * 2) / bounds.height, 2);
        const tx = W / 2 - scale * (bounds.x + bounds.width / 2);
        const ty = H / 2 - scale * (bounds.y + bounds.height / 2);
        svg.transition().duration(800).call(zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
      }
    }, 1800);

    return () => {
      simulation.stop(); zoomRef.current = null;
      nodeSelRef.current = null; edgeSelRef.current = null;
      d3.select(container).select('svg').remove(); style.remove();
    };
  }, [inputNodes, inputEdges, onNodeSelect, activeLayout]);

  // ── Category filter: show/hide nodes and their edges ──
  useEffect(() => {
    if (!nodeSelRef.current || !edgeSelRef.current) return;
    const hidden = hiddenTypes;

    nodeSelRef.current
      .transition().duration(200)
      .style('opacity', (d: any) => hidden.has(d.type) ? 0.08 : 1)
      .style('pointer-events', (d: any) => hidden.has(d.type) ? 'none' : 'auto');

    edgeSelRef.current
      .transition().duration(200)
      .style('opacity', (d: any) => {
        const sourceType = typeof d.source === 'object' ? d.source.type : null;
        const targetType = typeof d.target === 'object' ? d.target.type : null;
        if ((sourceType && hidden.has(sourceType)) || (targetType && hidden.has(targetType))) return 0.05;
        return 1;
      });

    // Restart simulation so remaining nodes redistribute
    if (simulationRef.current && physicsActive) {
      simulationRef.current.alphaTarget(0.15).restart();
      setTimeout(() => simulationRef.current?.alphaTarget(0), 600);
    }
  }, [hiddenTypes, physicsActive]);

  // ── Focus mode: dim non-neighbourhood nodes/edges ──
  useEffect(() => {
    if (!nodeSelRef.current || !edgeSelRef.current) return;
    const adj = adjRef.current;

    if (!focusedNodeId) {
      // Exit focus — restore all (respecting hidden types)
      nodeSelRef.current.transition().duration(300)
        .style('opacity', (d: any) => hiddenTypes.has(d.type) ? 0.08 : 1);
      edgeSelRef.current.transition().duration(300)
        .style('opacity', (d: any) => {
          const st = typeof d.source === 'object' ? d.source.type : null;
          const tt = typeof d.target === 'object' ? d.target.type : null;
          if ((st && hiddenTypes.has(st)) || (tt && hiddenTypes.has(tt))) return 0.05;
          return 1;
        });
      return;
    }

    const neighbours = adj[focusedNodeId] ?? new Set<string>();

    nodeSelRef.current.transition().duration(300)
      .style('opacity', (d: any) => {
        if (hiddenTypes.has(d.type)) return 0.02;
        if (d.id === focusedNodeId) return 1;
        if (neighbours.has(d.id)) return 1;
        return 0.10;
      });

    edgeSelRef.current.transition().duration(300)
      .style('opacity', (d: any) => {
        const sid = typeof d.source === 'object' ? d.source.id : d.source;
        const tid = typeof d.target === 'object' ? d.target.id : d.target;
        if (sid === focusedNodeId || tid === focusedNodeId) return 1;
        return 0.025;
      });
  }, [focusedNodeId, hiddenTypes]);

  // Escape exits focus
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFocusedNodeId(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Stats
  const feedsCount = inputEdges.filter(e => e.type === 'feeds' || e.type === 'FEEDS').length;

  const zoomIn = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    d3.select(svgRef.current).transition().duration(200).call(zoomRef.current.scaleBy, 1.3);
  }, []);

  const zoomOut = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    d3.select(svgRef.current).transition().duration(200).call(zoomRef.current.scaleBy, 0.77);
  }, []);

  const togglePhysics = useCallback(() => {
    setPhysicsActive(prev => {
      const next = !prev;
      if (simulationRef.current) {
        if (next) simulationRef.current.alphaTarget(0.3).restart();
        else { simulationRef.current.alphaTarget(0); simulationRef.current.stop(); }
      }
      return next;
    });
  }, []);

  const fitView = useCallback(() => {
    if (!svgRef.current || !containerRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    const zoomLayer = svg.select('.zoom-layer');
    const bounds = (zoomLayer.node() as SVGGElement)?.getBBox();
    const W = containerRef.current.clientWidth;
    const H = containerRef.current.clientHeight;
    if (bounds && bounds.width > 0) {
      const padding = 40;
      const scale = Math.min((W - padding * 2) / bounds.width, (H - padding * 2) / bounds.height, 2);
      const tx = W / 2 - scale * (bounds.x + bounds.width / 2);
      const ty = H / 2 - scale * (bounds.y + bounds.height / 2);
      svg.transition().duration(500).call(zoomRef.current.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
    }
  }, []);

  // ── Category filter toggle ──
  const toggleType = useCallback((type: string) => {
    setHiddenTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  // Unique node types present in current graph
  const presentTypes = useMemo(() => {
    const types = new Set(inputNodes.map(n => n.type));
    // Group aliases: hub = theatre, construct = template
    return Array.from(types).sort();
  }, [inputNodes]);

  const anyFiltered = hiddenTypes.size > 0;

  // Toolbar button style
  const tbtn = (active: boolean) =>
    `flex items-center justify-center w-7 h-7 rounded transition-colors ${active ? 'bg-[oklch(0.195_0.018_265)] text-[oklch(0.920_0.006_265)]' : 'text-[oklch(0.480_0.007_265)] hover:text-[oklch(0.690_0.008_265)] hover:bg-[oklch(0.148_0.014_265)]'}`;

  return (
    <div className={`relative w-full h-full ${className ?? ''}`}>
      <div ref={containerRef} className="w-full h-full" />

      {/* Focus mode indicator */}
      {focusedNodeId && (
        <div className="absolute top-3 left-3 z-10 px-3 py-1.5 rounded text-[10px] font-mono"
          style={{ background: 'oklch(0.195 0.018 265)', color: 'oklch(0.920 0.006 265)', border: '1px solid oklch(0.310 0.020 265)' }}>
          FOCUS: {inputNodes.find(n => n.id === focusedNodeId)?.label ?? focusedNodeId}
          <button onClick={() => setFocusedNodeId(null)} className="ml-2 opacity-50 hover:opacity-100">Esc</button>
        </div>
      )}

      {/* Legend */}
      <div className="absolute top-3 right-3 z-10 rounded-lg px-3 py-2 space-y-0.5"
        style={{ background: 'oklch(0.118 0.013 265 / 0.92)', border: '1px solid oklch(0.225 0.014 265)', backdropFilter: 'blur(8px)' }}>
        {Object.entries(NODE_CLASSES).filter(([k]) => !['hub', 'theatre', 'template'].includes(k)).slice(0, 8).map(([key, cls]) => (
          <div key={key} className="flex items-center gap-2 text-[9px] font-mono" style={{ color: 'oklch(0.690 0.008 265)' }}>
            <div className="w-2 h-2 flex-shrink-0" style={{ background: cls.color, borderRadius: key === 'knowledge' ? '2px' : '50%' }} />
            <span className="uppercase tracking-wider">{cls.label}</span>
          </div>
        ))}
        <div className="h-px my-1" style={{ background: 'oklch(0.175 0.012 265)' }} />
        <div className="flex items-center gap-2 text-[9px] font-mono" style={{ color: 'oklch(0.668 0.162 175)' }}>
          <div className="w-3 h-0.5 rounded-full" style={{ background: 'oklch(0.668 0.162 175)' }} />FEEDS
        </div>
        <div className="flex items-center gap-2 text-[9px] font-mono" style={{ color: 'oklch(0.440 0.110 258)' }}>
          <svg width="12" height="2"><line x1="0" y1="1" x2="12" y2="1" stroke="oklch(0.440 0.110 258)" strokeWidth="1" strokeDasharray="2,2" /></svg>
          Lineage
        </div>
        <div className="flex items-center gap-2 text-[9px] font-mono" style={{ color: 'oklch(0.530 0.205 295)' }}>
          <div className="w-3 h-0.5 rounded-full" style={{ background: 'oklch(0.530 0.205 295)' }} />Orchestration
        </div>
        <div className="flex items-center gap-2 text-[9px] font-mono" style={{ color: 'oklch(0.688 0.165 72)' }}>
          <div className="w-3 h-0.5 rounded-full" style={{ background: 'oklch(0.688 0.165 72)' }} />Proof
        </div>
      </div>

      {/* Bottom toolbar */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-0.5 px-1.5 py-1 rounded-lg"
        style={{ background: 'oklch(0.118 0.013 265 / 0.95)', border: '1px solid oklch(0.225 0.014 265)', backdropFilter: 'blur(8px)' }}>
        {/* Select + Pan */}
        <button className={tbtn(activeTool === 'select')} title="Select" onClick={() => setActiveTool('select')}><svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 1L2 10L5 7.5L7 11L8.5 10.3L6.5 6.5L10 6L2 1Z" fill="currentColor" /></svg></button>
        <button className={tbtn(activeTool === 'pan')} title="Pan" onClick={() => setActiveTool('pan')}><svg width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="2" fill="none" stroke="currentColor" strokeWidth="1.2" /><line x1="6" y1="1" x2="6" y2="3.5" stroke="currentColor" strokeWidth="1.2" /><line x1="6" y1="8.5" x2="6" y2="11" stroke="currentColor" strokeWidth="1.2" /><line x1="1" y1="6" x2="3.5" y2="6" stroke="currentColor" strokeWidth="1.2" /><line x1="8.5" y1="6" x2="11" y2="6" stroke="currentColor" strokeWidth="1.2" /></svg></button>
        <div className="w-px h-4 mx-1" style={{ background: 'oklch(0.225 0.014 265)' }} />

        {/* Zoom */}
        <button className={tbtn(false)} title="Zoom in" onClick={zoomIn}><svg width="12" height="12" viewBox="0 0 12 12"><circle cx="5" cy="5" r="3.5" stroke="currentColor" strokeWidth="1.2" fill="none" /><line x1="5" y1="3" x2="5" y2="7" stroke="currentColor" strokeWidth="1.2" /><line x1="3" y1="5" x2="7" y2="5" stroke="currentColor" strokeWidth="1.2" /><line x1="7.8" y1="7.8" x2="11" y2="11" stroke="currentColor" strokeWidth="1.2" /></svg></button>
        <button className={tbtn(false)} title="Zoom out" onClick={zoomOut}><svg width="12" height="12" viewBox="0 0 12 12"><circle cx="5" cy="5" r="3.5" stroke="currentColor" strokeWidth="1.2" fill="none" /><line x1="3" y1="5" x2="7" y2="5" stroke="currentColor" strokeWidth="1.2" /><line x1="7.8" y1="7.8" x2="11" y2="11" stroke="currentColor" strokeWidth="1.2" /></svg></button>
        <button className={tbtn(false)} title="Fit view" onClick={fitView}><svg width="12" height="12" viewBox="0 0 12 12"><rect x="2" y="2" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.2" fill="none" /><circle cx="6" cy="6" r="1.5" fill="currentColor" opacity="0.5" /></svg></button>
        <div className="w-px h-4 mx-1" style={{ background: 'oklch(0.225 0.014 265)' }} />

        {/* Layout popup button */}
        <div className="relative">
          <button className={tbtn(layoutMenuOpen)} title="Graph layout" onClick={() => { setLayoutMenuOpen(!layoutMenuOpen); setFilterMenuOpen(false); setAddMenuOpen(false); }}>
            <svg width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="1.8" fill="currentColor" opacity="0.7" /><circle cx="2" cy="2" r="1.2" fill="currentColor" opacity="0.5" /><circle cx="10" cy="2" r="1.2" fill="currentColor" opacity="0.5" /><circle cx="2" cy="10" r="1.2" fill="currentColor" opacity="0.5" /><circle cx="10" cy="10" r="1.2" fill="currentColor" opacity="0.5" /><line x1="3.2" y1="3.2" x2="4.8" y2="4.8" stroke="currentColor" strokeWidth="0.8" opacity="0.4" /><line x1="8.8" y1="3.2" x2="7.2" y2="4.8" stroke="currentColor" strokeWidth="0.8" opacity="0.4" /></svg>
          </button>
          {layoutMenuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setLayoutMenuOpen(false)} />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-40 rounded-lg py-1 z-40"
                style={{ background: 'oklch(0.118 0.013 265)', border: '1px solid oklch(0.225 0.014 265)', boxShadow: '0 6px 28px oklch(0 0 0 / 0.6)' }}>
                <div className="px-3 py-1 text-[8px] font-mono uppercase tracking-widest" style={{ color: 'oklch(0.380 0.007 265)' }}>Graph Layout</div>
                {['Structure', 'Radial', 'Spine', 'Memory Cluster'].map(l => (
                  <button key={l} onClick={() => { setActiveLayout(l); setLayoutMenuOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-[10px] font-mono transition-colors hover:bg-[oklch(0.148_0.014_265)] ${activeLayout === l ? 'text-[oklch(0.920_0.006_265)] bg-[oklch(0.195_0.018_265)]' : 'text-[oklch(0.690_0.008_265)]'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Category filter popup */}
        <div className="relative">
          <button className={tbtn(filterMenuOpen || anyFiltered)} title="Filter by type"
            onClick={() => { setFilterMenuOpen(!filterMenuOpen); setLayoutMenuOpen(false); setAddMenuOpen(false); }}
            style={anyFiltered ? { color: 'oklch(0.668 0.162 175)' } : undefined}>
            <svg width="12" height="12" viewBox="0 0 12 12">
              <path d="M1 2.5h10M3 6h6M5 9.5h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
            </svg>
          </button>
          {filterMenuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setFilterMenuOpen(false)} />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-44 rounded-lg py-1 z-40"
                style={{ background: 'oklch(0.118 0.013 265)', border: '1px solid oklch(0.225 0.014 265)', boxShadow: '0 6px 28px oklch(0 0 0 / 0.6)' }}>
                <div className="flex items-center justify-between px-3 py-1">
                  <span className="text-[8px] font-mono uppercase tracking-widest" style={{ color: 'oklch(0.380 0.007 265)' }}>Node Types</span>
                  {anyFiltered && (
                    <button onClick={() => setHiddenTypes(new Set())}
                      className="text-[8px] font-mono text-[oklch(0.668_0.162_175)] hover:underline">
                      Show all
                    </button>
                  )}
                </div>
                {presentTypes.map((type: string) => {
                  const cls = NODE_CLASSES[type];
                  if (!cls) return null;
                  const isVisible = !hiddenTypes.has(type);
                  return (
                    <button key={type} onClick={() => toggleType(type)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-[10px] font-mono transition-colors hover:bg-[oklch(0.148_0.014_265)]">
                      <div className="w-3 h-3 rounded flex items-center justify-center flex-shrink-0"
                        style={{ border: `1.5px solid ${isVisible ? cls.color : 'oklch(0.280 0.007 265)'}`, background: isVisible ? cls.color + '22' : 'transparent' }}>
                        {isVisible && (
                          <svg width="8" height="8" viewBox="0 0 8 8"><path d="M1.5 4L3.2 5.8L6.5 2.2" stroke={cls.color} strokeWidth="1.3" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        )}
                      </div>
                      <span style={{ color: isVisible ? 'oklch(0.690 0.008 265)' : 'oklch(0.380 0.007 265)' }}>{cls.label}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Add Node '+' (Figma-style composition picker) */}
        <div className="relative">
          <button className={tbtn(addMenuOpen)} title="Add node"
            onClick={() => { setAddMenuOpen(!addMenuOpen); setFilterMenuOpen(false); setLayoutMenuOpen(false); }}>
            <svg width="12" height="12" viewBox="0 0 12 12">
              <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" fill="none" />
              <line x1="6" y1="3" x2="6" y2="9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              <line x1="3" y1="6" x2="9" y2="6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
          {addMenuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setAddMenuOpen(false)} />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 rounded-lg z-40 overflow-hidden"
                style={{ background: 'oklch(0.118 0.013 265)', border: '1px solid oklch(0.225 0.014 265)', boxShadow: '0 8px 32px oklch(0 0 0 / 0.6)' }}>
                <div className="px-3 pt-2 pb-1">
                  <div className="text-[8px] font-mono uppercase tracking-widest" style={{ color: 'oklch(0.380 0.007 265)' }}>Add Module</div>
                </div>
                <div className="px-1 pb-2 grid grid-cols-2 gap-0.5">
                  {[
                    { kind: 'theatre_construct_draft', label: 'Theatre Draft' },
                    { kind: 'proposal_source', label: 'FORGE' },
                    { kind: 'miroshark_simulation', label: 'MiroShark' },
                    { kind: 'autoresearch_module', label: 'Theatre Calibrator' },
                    { kind: 'paradox_scanner', label: 'Paradox Scanner' },
                    { kind: 'calibrate_tool', label: 'Calibrate' },
                    { kind: 'scenario_pack', label: 'Scenario Pack' },
                    { kind: 'agent_actor', label: 'Agent Actor' },
                  ].map(mod => (
                    <button key={mod.kind}
                      className="flex items-center gap-1.5 px-2 py-1.5 rounded text-left transition-colors hover:bg-[oklch(0.148_0.014_265)]"
                      onClick={() => { onPlaceModule?.(mod.kind); setAddMenuOpen(false); }}>
                      <div className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                        style={{ background: 'oklch(0.548 0.172 198 / 0.12)', border: '1px solid oklch(0.548 0.172 198 / 0.2)' }}>
                        <div className="w-1 h-1 rounded-full" style={{ background: 'oklch(0.548 0.172 198)' }} />
                      </div>
                      <span className="text-[9px] font-mono truncate" style={{ color: 'oklch(0.690 0.008 265)' }}>{mod.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="w-px h-4 mx-1" style={{ background: 'oklch(0.225 0.014 265)' }} />

        {/* Physics */}
        <button className={tbtn(physicsActive)} title="Toggle physics" onClick={togglePhysics} style={physicsActive ? { color: '#22c55e' } : undefined}><svg width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="4" fill="none" stroke="currentColor" strokeWidth="1.2" /><circle cx="6" cy="6" r="1.5" fill="currentColor" opacity="0.6" /></svg></button>
      </div>

      {/* Status bar */}
      <div className="absolute bottom-4 right-4 z-10 flex items-center gap-3 px-3 py-1.5 rounded text-[9px] font-mono"
        style={{ background: 'oklch(0.118 0.013 265 / 0.9)', color: 'oklch(0.480 0.007 265)', border: '1px solid oklch(0.225 0.014 265)' }}>
        <span>{inputNodes.length} nodes</span>
        <span>{inputEdges.length} edges</span>
        <span style={{ color: 'oklch(0.668 0.162 175)' }}>{feedsCount} feeds</span>
        <span>{zoomPercent}%</span>
      </div>

      {/* Keyboard hints */}
      <div className="absolute bottom-14 left-4 z-10 text-[8px] font-mono leading-relaxed"
        style={{ color: 'oklch(0.480 0.007 265)', opacity: 0.2 }}>
        Scroll to zoom · Drag to pan<br />
        Click to inspect · Double-click to focus<br />
        Esc exit focus · 1-6 switch modes
      </div>
    </div>
  );
}
